// @fileoverview : 기준정보 > 외주처
import { mutateRoutingOutsources } from "@/api/routingOutsource/useRoutingOutsourcesQuery";
import { LocationDetailForm } from "@/features/location/detail/LocationDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { RoutingDetailForm } from "@/features/routing/components/form/RoutingDetailForm";
import { RoutingOutsourceCreateForm } from "@/features/RoutingOutsource/form/CreateForm";
import { RoutingOutsourceDetailForm } from "@/features/RoutingOutsource/form/DetailForm";
import { RoutingOutsourceUpdateForm } from "@/features/RoutingOutsource/form/UpdateForm";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import { ActionHeader } from "@/features/standardLayout/StandardInfoActionHeader";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import customAlert from "@/features/ui/alert/alert";
import { ConfirmForm } from "@/features/ui/confirm";
import { handleErrorResponse } from "@/utils/errorMessage";
import { ActionIcon, Avatar, Flex, Text, Tooltip } from "@mantine/core";
import {
  DefaultApiRoutingOutsourcesRoutingOutsourceIdDeleteRequest,
  RoutingOutsourcesGet200Response,
  RoutingOutsourcesGet200ResponseRowsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconEdit, IconFilterPlus, IconTrash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useMemo } from "react";
import { Column, SelectColumn } from "react-data-grid";
import { useTranslation } from "react-i18next";

export const RoutingOutsourcePage = () => {
  const { openModal, closeModal } = useModal();
  const { selectedRows, refetch } = useStandardLayout();
  const { t } = useTranslation();

  const { mutate: deleteMutate } = useMutation(
    (params: DefaultApiRoutingOutsourcesRoutingOutsourceIdDeleteRequest) =>
      mutateRoutingOutsources
        .delete(params)
        .mutationFn(params as DefaultApiRoutingOutsourcesRoutingOutsourceIdDeleteRequest | any),
    {
      onSuccess: () => {
        refetch();
        customAlert(
          `${t("message.deleteSuccess", { item: `${t("outsource.outsource")}` })}`,
          t("common.deleteSuccess"),
          "green",
        );
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("outsource.code") }),
          `${t("common.deleteFail", { item: `${t("outsource.outsource")}` })}`,
          "red",
        );
      },
    },
  );

  const columns: readonly Column<RoutingOutsourcesGet200ResponseRowsInner>[] = useMemo(
    () => [
      {
        ...SelectColumn,
        resizable: true,
      },
      {
        key: "name",
        name: t("outsource.name"),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return (
            <LinkAnchor
              onClick={() =>
                openModal(
                  <RoutingOutsourceDetailForm name={formatterProps.row.name} />,
                  null,
                  "",
                  true,
                )
              }
            >
              {formatterProps.row.name}
            </LinkAnchor>
          );
        },
      },
      {
        key: "routingCode",
        name: t("routing.code"),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return (
            <LinkAnchor
              onClick={() =>
                openModal(
                  <RoutingDetailForm routingCode={formatterProps.row.routingCode} />,
                  null,
                  "",
                  true,
                )
              }
            >
              {formatterProps.row.routingCode}
            </LinkAnchor>
          );
        },
      },
      {
        key: "fromLocationName",
        name: t("outsource.fromLocationName"),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return (
            <LinkAnchor
              onClick={() =>
                openModal(
                  <LocationDetailForm locationCode={formatterProps.row.fromLocationCode} />,
                  null,
                  "",
                )
              }
            >
              {formatterProps.row.fromLocationName}
            </LinkAnchor>
          );
        },
      },
      {
        key: "toLocationName",
        name: t("outsource.toLocationName"),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return (
            <LinkAnchor
              onClick={() =>
                openModal(
                  <LocationDetailForm locationCode={formatterProps.row.toLocationCode} />,
                  null,
                  "",
                )
              }
            >
              {formatterProps.row.toLocationName}
            </LinkAnchor>
          );
        },
      },
      {
        key: "options",
        name: t("common.action"),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          const updateRoutingOutsourceActionView = () => {
            openModal(
              <RoutingOutsourceUpdateForm formatterProps={formatterProps?.row} />,
              null,
              `${t("common.edit", { item: `${t("outsource.outsource")}` })}`,
              true,
            );
          };

          const deleteRoutingOutsourceAction = () => {
            openModal(
              <ConfirmForm
                message={t("message.rowDelete")}
                yesCallback={() =>
                  deleteMutate({ routingOutsourceId: formatterProps?.row.id as number })
                }
                noCallback={() => {}}
                commonCallback={() => closeModal(null)}
              />,
              null,
              `${t("common.delete", { item: `${t("outsource.outsource")}` })}`,
            );
          };

          return (
            <Flex w="100%" h="100%" justify="center" align="center" gap="md">
              <ActionIcon
                variant="subtle"
                onClick={updateRoutingOutsourceActionView}
                color="yellow"
              >
                <IconEdit />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                onClick={() => deleteRoutingOutsourceAction()}
                color="red"
              >
                <IconTrash />
              </ActionIcon>
            </Flex>
          );
        },
      },
    ],
    [closeModal, deleteMutate, openModal, t],
  );

  const handleDelete = () => {
    if ([...selectedRows].length === 0) {
      customAlert(
        `${t("message.deleteSelect", { item: `${t("outsource.outsource")}` })}`,
        `${t("common.deleteFail", { item: `${t("outsource.outsource")}` })}`,
        "red",
      );
      return;
    }

    openModal(
      <ConfirmForm
        message={t("message.rowDelete")}
        yesCallback={() => {
          [...selectedRows].forEach((row) => {
            deleteMutate({ routingOutsourceId: row });
          });

          selectedRows.clear();
        }}
        noCallback={() => {}}
        commonCallback={() => closeModal(null)}
      />,
      null,
      `${t("common.delete", { item: `${t("outsource.outsource")}` })}`,
    );
  };

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <Flex p="sm" justify="space-between" rowGap="md">
        <ActionHeader
          createFn={() =>
            openModal(
              <RoutingOutsourceCreateForm />,
              null,
              `${t("common.additional", { item: `${t("outsource.outsource")}` })}`,
              true,
            )
          }
          createDataTitle={t("outsource.outsource")}
          deleteFn={() => handleDelete()}
        />
      </Flex>
      <Flex bg="white" px="sm" direction="column" gap="xs">
        <Text fz="xl" fw={600}>
          {t("common.list", { item: `${t("outsource.outsource")}` })}
        </Text>
        <Text fz="xs">{t("common.subTitle", { item: `${t("outsource.outsource")}` })}</Text>
      </Flex>
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={t("common.filterDescription")}>
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<RoutingOutsourcesGet200Response, RoutingOutsourcesGet200ResponseRowsInner>
            columns={columns}
          />
        </StandardLayoutTableWrapper>
        <PaginationBox<RoutingOutsourcesGet200Response> />
      </Flex>
    </Flex>
  );
};
